import { slugify } from "./slugUtils";

/**
 * Set of CSV column headers that represent *multi-value* fields.
 *
 * IMPORTANT:
 * Values in this set must exactly match the CSV header names
 * defined in `CSV_FILE_HEADERS`.
 */
export const MULTI_VALUE_FIELDS = new Set([
  'Source', 'Countries and territories applied in', 'Use case', 'Theme', 
  'Technology', 'Target', 'Business model', 'Users', 'Food value chain stage', 'Four betters', 'SDGs',
]);

/**
 * Mapping between internal filter keys and short, filename-safe labels.
 *
 * Purpose:
 * Used exclusively when generating CSV filenames to describe
 * which filters are active in a compact and readable form.
 *
 * Example:
 * - Internal filter key: `use_case`
 * - Filename segment: `usecase-2`
 *
 * Behavior:
 * - Each active filter contributes `<label>-<count>` to the filename
 * - If a filter key is not present here, the raw key is used as fallback
 *
 */
export const FILTER_LABELS: Record<string, string> = {
  country: 'country', origin_country: 'origin', type: 'type', use_case: 'usecase', 
  theme: 'theme', technology: 'tech', users: 'target', business_model: 'bm', fvc: 'fvc', 
  maturity: 'maturity', organization_type: 'orgtype', four_betters: '4betters', sdg: 'sdg',
};

export const CSV_FILE_HEADERS = [
      'Title', 'Short description', 'Entry Type', 'Source', 'Organization', 'Organization type', 'Website', 
      'Country of origin', 'Countries and territories applied in', 'Timeframe', 'Maturity', 'Use case',
      'Theme', 'Technology', 'Target', 'Business model', 'Food value chain stage', 'Four betters', 'SDGs', 
      'Description', 'Technology description', 'Target description', 'Business model description', 'Impact', 
      'Last update', 'Link to the Observatory',
    ];

export const CSV_FIELD_MAP: Record<string, string | null> = {
  'Title': 'name', 'Short description': 'sub_name', 'Entry Type': 'type', 'Source': 'source', 
  'Organization': 'organization', 'Organization type': 'organization_type', 'Website': 'website',
  'Country of origin': 'country_of_origin', 'Countries and territories applied in': 'country_or_territory', 
  'Timeframe': 'implemented_since', 'Maturity': 'maturity', 'Use case': 'use_case', 'Theme': 'theme', 'Technology': 'technology', 
  'Target': 'users', 'Business model': 'business_model', 'Food value chain stage': 'food_value_chain_stage', 'Four betters': 'four_betters',
  'SDGs': 'sdgs', 'Description': 'description', 'Technology description': 'technology_description',
  'Target description': 'user_description', 'Business model description': 'business_model_description',
  'Impact': 'impact', 'Last update': 'date', 'Link to the Observatory': null, // intentionally unmapped
};

/**
 * Escapes a single value for safe inclusion in a CSV file according to
 * RFC-4180 rules.
 *
 * Behavior:
 * - `null` and `undefined` values are converted to an empty string.
 * - Array values are serialized as JSON arrays (e.g. `["a","b"]`) before
 *   being escaped, to preserve multi-value fields.
 * - All other values are coerced to strings.
 * - Any double quotes inside the value are escaped by doubling them (`"` → `""`).
 * - The resulting value is always wrapped in double quotes.
 *
 * @param value - The value to escape.
 * @returns A CSV-safe, double-quoted string representation of the value.
 */
export const csvEscapeField = (value: unknown): string => {
  let s = '';

  if (value === null || value === undefined) {
    s = '';
  } else if (Array.isArray(value)) {
    s = JSON.stringify(value);
  } else {
    s = String(value);
  }
  s = s.replace(/"/g, '""');
  return `"${s}"`;
};

/**
 * Converts an array of objects into a CSV-formatted string.
 *
 * Behavior:
 * - The order of columns is defined by the `headers` array.
 * - Each cell value is escaped using `csvEscapeField`
 * - Rows are separated by (`\r\n`) line endings.
 *
 * @param rows - The data rows to serialize, expressed as objects keyed by header name.
 * @param headers - The ordered list of column headers to include in the CSV.
 * @returns A complete CSV string, including header and data rows.
 */
export const toCsv = (rows: Record<string, unknown>[], headers: string[]): string => {
  const headerRow = headers.map((h) => csvEscapeField(h)).join(',');
  const dataRows = rows.map((row) =>
    headers.map((h) => csvEscapeField(row[h])).join(',')
  );
  return [headerRow, ...dataRows].join('\r\n');
};



export const downloadCsv = (csv: string, filename: string) => {
  const csvWithBom = '\ufeff' + csv;
  const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
};

/**
 * Normalizes a value for CSV export, handling potential multi-value fields.
 */
export const normalizeMaybeMultiValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return '';
  
  // If the value is already an array, return it as-is
  if (Array.isArray(value)) return value;
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.includes(';')) {
      const parts = trimmed.split(';').map((p) => p.trim()).filter(Boolean);
      return parts.length > 1 ? parts : (parts[0] ?? '');
    }

    return trimmed;
  }

  return value;
};

export const normalizeForCsv = (header: string, value: unknown): unknown => {
  if (!MULTI_VALUE_FIELDS.has(header)) {
    return value ?? '';
  }
  return normalizeMaybeMultiValue(value);
};

/**
 * Builds a compact, filename-safe summary.
 *
 * This function inspects the active UI filters and search term and derives:
 * - `mode`: whether the dataset represents a full export or a filtered export
 * - `indicator`: a short, deterministic string describing which filters
 *   are applied, suitable for use in filenames
 *
 * Behavior:
 * - Each filter with at least one selected value contributes a segment in
 *   the form `<filterKey>-<count>` (e.g. `country-2`, `theme-1`)
 * - If a non-empty search term is present, `search-yes` is appended
 * - If no filters and no search term are applied:
 *   - `mode` is `'full'`
 *   - `indicator` is `'no-filters'`
 * - Otherwise:
 *   - `mode` is `'filtered'`
 *   - `indicator` is the underscore-separated list of segments
 *
 */
export const buildFilterIndicator = (
  selectedFilters: Filters,
  searchTerm: string
): { mode: 'full' | 'filtered'; indicator: string } => {

  const parts: string[] = [];

  Object.entries(selectedFilters).forEach(([key, val]) => {
    if (Array.isArray(val) && val.length > 0) {
      const label = FILTER_LABELS[key] ?? key;
      parts.push(`${label}-${val.length}`);
    }
  });

  if ((searchTerm || '').trim().length > 0) parts.push('search-yes');
  const mode: 'full' | 'filtered' = parts.length === 0 ? 'full' : 'filtered';
  const indicator = parts.length === 0 ? 'no-filters' : parts.join('_');
  return { mode, indicator };
};

export const buildCsvRows = (dataSource: any[]): Record<string, unknown>[] => (dataSource || []).map((it) => {
    const row: Record<string, unknown> = {};
      CSV_FILE_HEADERS.forEach((header) => {
        const sourceKey = CSV_FIELD_MAP[header];

        if (sourceKey === null && header === 'Link to the Observatory') {
          const slug = it?.name ? slugify(it.name) : '';
          row[header] = slug
            ? `https://agritechobservatory.fao.org/${slug}`
            : '';
          return;
        }



        row[header] = sourceKey === null
          ? ''
          : normalizeForCsv(header, it?.[sourceKey]);
      });

    return row;
  });

export const exportInitiativesCsv = (args: {
  dataSource: any[];
  selectedFilters: Filters;
  searchTerm: string;
  prefix?: string;
}) => {
  const rows = buildCsvRows(args.dataSource);
  const csv = toCsv(rows, CSV_FILE_HEADERS);

  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toTimeString().slice(0, 5).replace(':', '');

  const filename = `AgriTech Observatory_${date}_${time}.csv`;

  downloadCsv(csv, filename);
};

